import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore'
import { db } from '@/core/firebase/client'
import { FirestoreRepository } from '@/core/firebase/repository'
import { clubSubcollections } from '@/core/firebase/paths'
import { CourtSchema, type Court, type PricingRule } from '@/core/models/court'
import { logger } from '@/core/logger'

/**
 * Best-effort write of `clubs/{clubId}.pricesReviewedAt` — used by the
 * post-claim tutorial to tick its "Revisa precios y pistas" step. We only
 * test `!= null` so overwriting on every edit is fine; failures are
 * silent because this is purely UI-state and shouldn't block the price
 * write itself.
 */
async function markPricesReviewed(clubId: string): Promise<void> {
  if (!clubId) return
  try {
    const clubRef = doc(db, 'clubs', clubId)
    await updateDoc(clubRef, { pricesReviewedAt: serverTimestamp() })
  } catch (e) {
    logger.w('markPricesReviewed failed', e)
  }
}

/**
 * Thrown by `setActive(courtId, false)` when the court has upcoming
 * non-cancelled bookings. The caller (UI) can catch and show a confirmation
 * asking "Pista tiene N reservas próximas — ¿desactivar igualmente?" before
 * calling `setActive(courtId, false, { force: true })`.
 */
export class CourtHasActiveBookingsError extends Error {
  readonly count: number
  constructor(count: number) {
    super(`Court has ${count} upcoming non-cancelled bookings`)
    this.name = 'CourtHasActiveBookingsError'
    this.count = count
  }
}

export interface CreateCourtPayload {
  name: string
  location?: Court['location']
  wallType?: Court['wallType']
  floorSurface?: Court['floorSurface']
  hasLighting?: boolean
  size?: Court['size']
  sport?: Court['sport']
  /** Explicit player capacity. Null / absent → falls back to size-derived
   *  default via `effectiveCourtCapacity`. */
  capacity?: Court['capacity']
  sortOrder?: number
  status?: Court['status']
}

export class CourtsRepository extends FirestoreRepository<typeof CourtSchema> {
  constructor(clubId: string) {
    super(clubSubcollections.courts(clubId), CourtSchema)
  }

  /** All courts (active + inactive), ordered by sortOrder ascending. */
  listAll(): Promise<Court[]> {
    return this.list(orderBy('sortOrder', 'asc'))
  }

  /**
   * Courts visible on the schedule — everything except `status: 'inactive'`.
   * Maintenance courts ARE returned (they need to render on the grid with
   * their striped indicator). Filters client-side because lists are tiny.
   */
  async listActive(): Promise<Court[]> {
    const all = await this.listAll()
    return all.filter((c) => c.status !== 'inactive')
  }

  /** Create a new court under this club. Returns the new doc id. */
  async createCourt(input: CreateCourtPayload): Promise<string> {
    const clubId = this.path.split('/')[1] ?? ''
    const col = collection(db, this.path)
    const ref = doc(col)
    await setDoc(ref, {
      clubId,
      name: input.name,
      location: input.location ?? 'indoor',
      wallType: input.wallType ?? 'crystal',
      floorSurface: input.floorSurface ?? 'artificialTurf',
      hasLighting: input.hasLighting ?? true,
      size: input.size ?? 'normal',
      sport: input.sport ?? 'padel',
      ...(input.capacity != null ? { capacity: input.capacity } : {}),
      status: input.status ?? 'active',
      sortOrder: input.sortOrder ?? 0,
      pricingRules: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
    return ref.id
  }

  /**
   * Patch court fields. Caller passes only the fields that change.
   *
   * Guard: if the patch flips status to `'inactive'`, refuse if the court
   * has non-cancelled bookings in the next 14 days unless the caller passes
   * `{ force: true }`. Mirrors the protection that lived in `setStatus`
   * — keeps the safety in one place regardless of which surface initiates
   * the change.
   */
  async updateCourt(
    courtId: string,
    patch: Partial<Court>,
    options: { force?: boolean; updatedByUid?: string | null } = {},
  ): Promise<void> {
    if (patch.status === 'inactive' && !options.force) {
      const count = await this.countUpcomingBookings(courtId, 14)
      if (count > 0) throw new CourtHasActiveBookingsError(count)
    }
    const ref = doc(db, this.path, courtId)
    await updateDoc(ref, {
      ...patch,
      ...(options.updatedByUid !== undefined ? { updatedByUid: options.updatedByUid } : {}),
      updatedAt: serverTimestamp(),
    })
    const clubId = this.path.split('/')[1] ?? ''
    await markPricesReviewed(clubId)
  }

  /**
   * Replace the full pricing-rules list for a court. Rules are stored as a
   * Map<id, rule> in Firestore (see `pricingRulesMapToArray` preprocess in
   * court.ts) — convert back here so the document shape matches what the
   * the client writes.
   */
  async updatePricingRules(
    courtId: string,
    rules: PricingRule[],
    updatedByUid?: string | null,
  ): Promise<void> {
    const rulesMap: Record<string, Omit<PricingRule, 'id'>> = {}
    for (const r of rules) {
      rulesMap[r.id] = {
        daysOfWeek: r.daysOfWeek,
        startTime: r.startTime,
        endTime: r.endTime,
        price: r.price,
        pricingMode: r.pricingMode,
        priority: r.priority,
        ...(r.startDate != null ? { startDate: r.startDate } : {}),
        ...(r.endDate != null ? { endDate: r.endDate } : {}),
      }
    }
    const ref = doc(db, this.path, courtId)
    await updateDoc(ref, {
      pricingRules: rulesMap,
      ...(updatedByUid !== undefined ? { updatedByUid } : {}),
      updatedAt: serverTimestamp(),
    })
    const clubId = this.path.split('/')[1] ?? ''
    await markPricesReviewed(clubId)
  }

  /**
   * Toggle a court between active and inactive.
   *
   * Deactivation guard: by default, refuses to deactivate a court that has
   * any non-cancelled bookings in the next 14 days (the operational window
   * that receptionists watch). The caller can override with `force: true`
   * after confirming with the operator — typical flow for scheduled
   * maintenance where existing bookings will be manually moved.
   *
   * Throws `CourtHasActiveBookingsError` if guard trips.
   */
  async setStatus(
    courtId: string,
    status: 'active' | 'maintenance' | 'inactive',
    options: { force?: boolean; updatedByUid?: string | null } = {},
  ): Promise<void> {
    // Guard now lives in updateCourt — pass all options straight through.
    return this.updateCourt(courtId, { status }, options)
  }

  /**
   * Count non-cancelled bookings on a court within the next `daysAhead` days.
   * Lightweight — uses the bookings subcollection under the same club root.
   */
  async countUpcomingBookings(courtId: string, daysAhead: number): Promise<number> {
    const clubId = this.path.split('/')[1] ?? ''
    if (!clubId) return 0
    const now = new Date()
    const end = new Date()
    end.setDate(end.getDate() + daysAhead)
    const bookingsPath = `clubs/${clubId}/bookings`
    const q = query(
      collection(db, bookingsPath),
      where('courtId', '==', courtId),
      where('startDateTime', '>=', Timestamp.fromDate(now)),
      where('startDateTime', '<=', Timestamp.fromDate(end)),
    )
    const snap = await getDocs(q)
    let count = 0
    for (const d of snap.docs) {
      if ((d.data()?.['status'] as string) !== 'cancelled') count += 1
    }
    return count
  }
}
